import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'Services',
    href: '/services'
  }, {
    name: 'Insights',
    href: '/insights'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  // Use a transparent header only over the dark Home hero. Every other route
  // begins with a light navigation surface for consistent readability.
  const isHome = location.pathname === '/';
  const isWhiteHeader = isScrolled || !isHome;
  const navStyle = isWhiteHeader ? 'bg-brand-warm-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent pt-3 pb-7';
  const textStyle = isWhiteHeader ? 'text-brand-navy hover:text-brand-gold' : 'text-white hover:text-brand-gold';
  const logoStyle = isWhiteHeader ? '' : 'invert brightness-0';
  const logoSrc = 'https://cdn.magicpatterns.com/uploads/7fqogpQfYe6LJMG1Tmc1x4/ChatGPT_Image_Jul_13,_2026,_06_34_58_PM.png';
  return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${navStyle}`} data-id="element-71" data-mp="w5ve4gtc4dn">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-72" data-mp="5mm97pojps0j">
        <Link to="/" className="flex items-center gap-2" data-id="element-73" data-mp="5opaj5v5f7kk">
          <img src={logoSrc} alt="Consilium Advisory Logo" className={`h-32 w-auto object-contain transition-all duration-500 ${logoStyle}`} data-id="element-74" data-mp="34c39goi3hwh9" />
        </Link>

        <div className="hidden md:flex items-center gap-8" data-id="element-75" data-mp="319yny9xdrg7p">
          {navLinks.map(link => {
          const isActive = location.pathname === link.href;
          return <Link key={link.name} to={link.href} className={`text-[11px] font-bold tracking-[0.15em] uppercase transition-all duration-300 relative group ${isActive ? 'text-brand-gold' : textStyle}`} data-id="element-76" data-mp="3b01fs6srmo4g">
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[1.5px] bg-brand-gold transition-all duration-300 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-77" data-mp="2de416khib2xs" />
              </Link>;
        })}
        </div>

        <div className="hidden md:block" data-id="element-78" data-mp="2ndux8qlptpzz">
          <Link to="/contact" data-id="element-79" data-mp="7jb7fl9pnyzl">
            <button className="bg-brand-gold hover:bg-brand-gold/90 text-white px-6 py-3 text-sm font-semibold tracking-wider uppercase transition-all" data-id="element-80" data-mp="7ijbuag4619b">
              Schedule a Consultation
            </button>
          </Link>
        </div>

        <button className="md:hidden" onClick={() => setIsMobileMenuOpen(true)} data-id="element-81" data-mp="f3lp5b7um62o">
          <Menu size={28} className={isScrolled || !isHome ? 'text-brand-navy' : 'text-white'} data-id="element-82" data-mp="14vv64gbsmij" />
        </button>
      </div>

      <AnimatePresence data-id="element-83" data-mp="svjrn37213pv">
        {isMobileMenuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-brand-navy/40 backdrop-blur-sm z-[90] md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-84" data-mp="xuwg1sdkqiuk" />

            {/* Floating Menu Card */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: -20,
          filter: 'blur(10px)'
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          filter: 'blur(0px)'
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: -20,
          filter: 'blur(10px)'
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="fixed top-24 right-4 left-4 bg-white/95 backdrop-blur-xl shadow-2xl rounded-3xl z-[100] md:hidden overflow-hidden border border-white/20" data-id="element-85" data-mp="1gkunq7dgm9xo">
              <div className="p-6 flex flex-col gap-2" data-id="element-86" data-mp="bmr7w0lm7znw">
                {navLinks.map((link, i) => {
              const isActive = location.pathname === link.href;
              return <motion.div key={link.name} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.4,
                delay: i * 0.05,
                ease: 'easeOut'
              }} data-id="element-87" data-mp="1wzayzq1mug9" data-mpi={i}>
                      <Link to={link.href} className={`block px-4 py-3 rounded-xl text-lg font-bold tracking-wide transition-all ${isActive ? 'bg-brand-navy text-white' : 'text-brand-navy hover:bg-brand-navy/5'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-88" data-mp="pl8ufuzsb648">
                        {link.name}
                      </Link>
                    </motion.div>;
            })}
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.4,
            delay: 0.3
          }} className="p-6 bg-brand-warm-white border-t border-brand-navy/5" data-id="element-89" data-mp="2w5o7gqt4m62v">
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} data-id="element-90" data-mp="39x2rn0yrsdbi">
                  <button className="w-full bg-brand-gold text-white px-6 py-4 rounded-xl text-sm font-bold tracking-widest uppercase hover:bg-brand-navy transition-all shadow-lg shadow-brand-gold/20" data-id="element-91" data-mp="3evw3koyerw9k">
                    Schedule a Consultation
                  </button>
                </Link>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
};